<script setup>
import { h, ref, watch, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DurationInput from 'next/input/DurationInput.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const { t } = useI18n();
const duration = ref(0);
const unit = ref(DURATION_UNITS.MINUTES);
const message = ref('');
const labelToApply = ref({});
const ignoreWaiting = ref(false);
const includePending = ref(false);
const warningEnabled = ref(false);
const warningDuration = ref(0);
const warningUnit = ref(DURATION_UNITS.MINUTES);
const warningMessage = ref('');
const isEnabled = ref(false);
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

const labels = useMapGetter('labels/getLabels');

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value.map(label => ({
        id: label.title,
        name: label.title,
        icon: h('span', {
          class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
          style: { backgroundColor: label.color },
        }),
      }))
    : []
);

const selectedLabelName = computed(() => labelToApply.value?.name ?? null);

const inferUnit = value => {
  if (!value) return DURATION_UNITS.MINUTES;
  if (value % (24 * 60) === 0) return DURATION_UNITS.DAYS;
  if (value % 60 === 0) return DURATION_UNITS.HOURS;
  return DURATION_UNITS.MINUTES;
};

watch(
  [currentAccount, labelOptions],
  () => {
    const {
      auto_resolve_after,
      auto_resolve_message,
      auto_resolve_ignore_waiting,
      auto_resolve_include_pending,
      auto_resolve_label,
      auto_resolve_warning_enabled,
      auto_resolve_warning_after,
      auto_resolve_warning_message,
    } = currentAccount.value?.settings || {};

    duration.value = auto_resolve_after;
    message.value = auto_resolve_message;
    ignoreWaiting.value = !!auto_resolve_ignore_waiting;
    includePending.value = !!auto_resolve_include_pending;
    warningEnabled.value = !!auto_resolve_warning_enabled;
    warningDuration.value = auto_resolve_warning_after || 0;
    warningMessage.value = auto_resolve_warning_message || '';
    labelToApply.value = labelOptions.value.find(
      option => option.name === auto_resolve_label
    );

    unit.value = inferUnit(duration.value);
    warningUnit.value = inferUnit(warningDuration.value);

    if (duration.value) isEnabled.value = true;
  },
  { deep: true, immediate: true }
);

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const validate = () => {
  if (duration.value < 5) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.ERROR'));
    return false;
  }
  if (warningEnabled.value) {
    if (warningDuration.value < 1) {
      useAlert(
        t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.DURATION_ERROR_MIN')
      );
      return false;
    }
    if (warningDuration.value >= duration.value) {
      useAlert(
        t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.DURATION_ERROR_MAX')
      );
      return false;
    }
    if (!warningMessage.value.trim()) {
      useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.MESSAGE_ERROR'));
      return false;
    }
  }
  return true;
};

const handleSubmit = async () => {
  if (!validate()) return Promise.resolve();

  return updateAccountSettings({
    auto_resolve_after: duration.value,
    auto_resolve_message: message.value,
    auto_resolve_ignore_waiting: ignoreWaiting.value,
    auto_resolve_include_pending: includePending.value,
    auto_resolve_label: selectedLabelName.value,
    auto_resolve_warning_enabled: warningEnabled.value,
    auto_resolve_warning_after: warningEnabled.value
      ? warningDuration.value
      : null,
    auto_resolve_warning_message: warningEnabled.value
      ? warningMessage.value
      : '',
  });
};

const handleDisable = async () => {
  duration.value = null;
  message.value = '';
  warningEnabled.value = false;
  warningDuration.value = 0;
  warningMessage.value = '';
  includePending.value = false;

  return updateAccountSettings({
    auto_resolve_after: null,
    auto_resolve_message: '',
    auto_resolve_ignore_waiting: false,
    auto_resolve_include_pending: false,
    auto_resolve_label: null,
    auto_resolve_warning_enabled: false,
    auto_resolve_warning_after: null,
    auto_resolve_warning_message: '',
  });
};

const toggleAutoResolve = async () => {
  if (!isEnabled.value) handleDisable();
};
</script>

<template>
  <section class="flex flex-col w-full">
    <div class="flex items-start justify-between gap-4 mb-4">
      <div class="flex flex-col">
        <h3 class="text-sm font-medium text-n-slate-12 mb-1">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-0">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.NOTE') }}
        </p>
      </div>
      <Switch v-model="isEnabled" @change="toggleAutoResolve" />
    </div>

    <template v-if="isEnabled">
      <hr class="border-n-weak mb-6" />

      <form class="flex flex-col" @submit.prevent="handleSubmit">
        <div class="grid grid-cols-12 items-start gap-4">
          <div class="col-span-12 sm:col-span-4 pt-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL') }}:
            </label>
            <p class="text-xs text-n-slate-11 mt-1 mb-0">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.HELP') }}
            </p>
          </div>
          <div class="col-span-12 sm:col-span-8">
            <DurationInput
              v-model="duration"
              v-model:unit="unit"
              min="0"
              max="1438560"
              class="w-full"
            />
          </div>
        </div>

        <hr class="border-n-weak my-6" />

        <div class="grid grid-cols-12 items-start gap-4">
          <div class="col-span-12 sm:col-span-4 pt-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL') }}:
            </label>
            <p class="text-xs text-n-slate-11 mt-1 mb-0">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.HELP') }}
            </p>
          </div>
          <div class="col-span-12 sm:col-span-8">
            <TextArea
              v-model="message"
              class="w-full"
              :placeholder="
                t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')
              "
            />
          </div>
        </div>

        <hr class="border-n-weak my-6" />

        <div class="flex flex-col gap-4">
          <div class="flex items-start justify-between gap-4">
            <div class="flex flex-col">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.SECTION') }}
              </label>
              <p class="text-xs text-n-slate-11 mt-1 mb-0">
                {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.HELP') }}
              </p>
            </div>
            <Switch v-model="warningEnabled" />
          </div>

          <template v-if="warningEnabled">
            <div class="grid grid-cols-12 items-start gap-4">
              <div class="col-span-12 sm:col-span-4 pt-2">
                <label class="text-sm font-medium text-n-slate-12">
                  {{
                    t(
                      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.DURATION_LABEL'
                    )
                  }}:
                </label>
              </div>
              <div class="col-span-12 sm:col-span-8">
                <DurationInput
                  v-model="warningDuration"
                  v-model:unit="warningUnit"
                  min="0"
                  max="1438559"
                  class="w-full"
                />
              </div>
            </div>

            <div class="grid grid-cols-12 items-start gap-4">
              <div class="col-span-12 sm:col-span-4 pt-2">
                <label class="text-sm font-medium text-n-slate-12">
                  {{
                    t(
                      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.MESSAGE_LABEL'
                    )
                  }}:
                </label>
              </div>
              <div class="col-span-12 sm:col-span-8">
                <TextArea
                  v-model="warningMessage"
                  class="w-full"
                  :placeholder="
                    t(
                      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.MESSAGE_PLACEHOLDER'
                    )
                  "
                />
              </div>
            </div>
          </template>
        </div>

        <hr class="border-n-weak my-6" />

        <div class="flex flex-col gap-4">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.PREFERENCES') }}
          </label>

          <div class="flex items-center justify-between gap-4">
            <span class="text-sm text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL') }}
            </span>
            <Switch v-model="ignoreWaiting" />
          </div>

          <div class="flex items-center justify-between gap-4">
            <span class="text-sm text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.INCLUDE_PENDING.LABEL') }}
            </span>
            <Switch v-model="includePending" />
          </div>

          <div class="flex items-center justify-between gap-4">
            <span class="text-sm text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.LABEL') }}:
            </span>
            <SingleSelect
              v-model="labelToApply"
              :options="labelOptions"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.PLACEHOLDER')
              "
              placeholder-icon="i-lucide-chevron-down"
              placeholder-trailing-icon
              variant="faded"
            />
          </div>
        </div>

        <hr class="border-n-weak my-6" />

        <div class="flex">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.UPDATE_BUTTON')"
          />
        </div>
      </form>
    </template>
  </section>
</template>
